function mapResponseToSuggestion(paragraphs, response) {
  const original = paragraphs.find(p => response.id === p.id);
  if (!original || !response.part) {
    return;
  }
  return {
    original,
    suggestion: response.part
  };
}
export async function* suggestionGenerator({
  endpoint,
  paragraphs,
  payload,
  abortController
}) {
  try {
    const requestPayload = JSON.parse(payload);
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8'
      },
      body: JSON.stringify({
        ...requestPayload
      }),
      credentials: 'include',
      signal: abortController.signal,
      mode: 'cors'
    });
    if (!response.ok) {
      return yield {
        state: 'failed',
        reason: 'backend',
        error: `unexpected response status: ${response.status}`,
        statusCode: response.status
      };
    }
    if (!response.body) {
      yield {
        state: 'failed',
        reason: 'network',
        error: 'response.body missing',
        statusCode: response.status
      };
      return;
    }
    try {
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let done = false;
      while (!done) {
        const {
          value,
          done: doneReading
        } = await reader.read();
        done = doneReading;
        const chunkValue = decoder.decode(value);
        buffer = buffer + chunkValue;
        // Split the buffer by line breaks
        const lines = buffer.split('\n');
        // Process all complete lines, except for the last one (which might be incomplete)
        while (lines.length > 1) {
          const line = lines.shift();
          const parsedData = JSON.parse(line);
          const mappedData = mapResponseToSuggestion(paragraphs, parsedData);
          if (!mappedData) {
            yield {
              state: 'failed',
              reason: 'backend',
              error: (parsedData === null || parsedData === void 0 ? void 0 : parsedData.error) || 'backend',
              statusCode: response.status
            };
          } else {
            yield {
              state: 'parsed',
              suggestion: mappedData
            };
          }
        }
        // Keep the last (potentially incomplete) line in the buffer
        buffer = lines[0];
        if (done) {
          yield {
            state: 'done'
          };
          return;
        }
      }
      yield {
        state: 'done'
      };
      return;
    } catch (parsingError) {
      yield {
        state: 'failed',
        reason: 'parsing',
        error: parsingError,
        statusCode: response.status
      };
      return;
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      yield {
        state: 'failed',
        reason: 'aborted',
        error,
        statusCode: error.status
      };
    } else {
      yield {
        state: 'failed',
        reason: 'network',
        error,
        statusCode: error.status
      };
    }
    return;
  }
}

/**
 * Generator will yield whenever a promise is resolved
 * This is done for the MVP endpoint to match the generator expected in the ProactiveAIProvider
 */
export async function* resolvedMVPResponseGenerator({
  paragraphs,
  promises
}) {
  let doneCount = 0;
  while (doneCount !== paragraphs.length) {
    if (promises.length > 0) {
      for (let i = 0; i < promises.length; i++) {
        const response = await promises[i];
        if (response.error) {
          yield {
            state: 'failed',
            reason: 'backend',
            error: response.error,
            statusCode: response.statusCode
          };
        } else {
          if (!!(response !== null && response !== void 0 && response.response)) {
            yield {
              state: 'parsed',
              suggestion: {
                original: paragraphs[i],
                suggestion: response === null || response === void 0 ? void 0 : response.response
              }
            };
          } else {
            yield {
              state: 'failed',
              reason: 'backend',
              error: 'not fulfilled',
              statusCode: response.statusCode
            };
          }
        }
        doneCount++;
      }
    }
    if (doneCount === paragraphs.length) {
      yield {
        state: 'done'
      };
      return;
    }
  }
}